/**
 * useLabReports Hook
 *
 * TanStack Query hooks for fetching and uploading patient lab reports
 * stored as FHIR DocumentReference resources.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchLabReports,
  uploadLabReport,
  deleteLabReport,
  type LabReportRecord,
} from '@/lib/api/labReports';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('useLabReports');

/**
 * Fetch all lab reports for a patient.
 *
 * @param patientId - Patient FHIR ID; hook is disabled when undefined.
 */
export function useLabReports(patientId?: string) {
  return useQuery({
    queryKey: ['patient-documents', patientId],
    queryFn: async () => {
      if (!patientId) throw new Error('patientId is required');
      return fetchLabReports(patientId);
    },
    enabled: !!patientId,
    staleTime: 30_000, // 30s — metadata-only fetch is lightweight; cache updated optimistically on upload/delete
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    meta: {
      errorMessage: 'Failed to fetch lab reports',
    },
  });
}

/**
 * Upload a single lab report file for a patient.
 * On success, optimistically prepends the new record to the cached list.
 *
 * @param patientId - Patient FHIR ID; mutation throws when undefined.
 */
export function useUploadLabReport(
  patientId?: string,
  encounterId?: string,
  appointmentId?: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      if (!patientId) throw new Error('patientId is required to upload');
      return uploadLabReport(file, patientId, encounterId, appointmentId);
    },
    onSuccess: (newReport) => {
      logger.log('Lab report uploaded:', newReport.id);
      // Prepend to cached list so UI updates immediately
      queryClient.setQueryData<LabReportRecord[]>(['patient-documents', patientId], (old) => [
        newReport,
        ...(old ?? []),
      ]);
    },
    onError: (error) => {
      logger.error('Failed to upload lab report:', error);
    },
  });
}

/**
 * Delete a lab report for a patient.
 * On success, removes the record from the cached list.
 *
 * @param patientId - Patient FHIR ID; mutation throws when undefined.
 */
export function useDeleteLabReport(patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (docRefId: string) => {
      return deleteLabReport(docRefId);
    },
    onSuccess: (_data, docRefId) => {
      logger.log('Lab report deleted:', docRefId);
      // Remove from cached list
      queryClient.setQueryData<LabReportRecord[]>(['patient-documents', patientId], (old) =>
        (old ?? []).filter((r) => r.id !== docRefId)
      );
    },
    onError: (error) => {
      logger.error('Failed to delete lab report:', error);
    },
  });
}
